// Farm ATC simulation: flight plans, wind holds, conflict prediction, and the
// airborne separation engine. Pure state + functions, no DOM -- the UI reads
// the state each frame and calls these functions for player actions.
//
// Conflict prediction only warns: a filed plan launches at its departure time
// unless the player Delays or Reroutes it. Wind holds stay automatic -- they
// are a safety limit, not a decision.

import {
  DAY_LENGTH, SPAWN_INTERVAL_STEP,
  CONFLICT_DIST, COLLISION_DIST, PREDICT_DIST, ARRIVE_DIST,
  DELAY_STEP, WIND_RELEASE_LEAD, OPENING_PLANS, OPENING_LEAD, OPENING_STAGGER,
  MAX_WIND_INPUT, FIELDS, HUBS, TYPES, TIME_SLOTS, DEFAULT_WIND, SCORING,
  ORIENTATION_FIELDS, ORIENTATION_WIND_SPEED, ORIENTATION_WIND_DIR, LEVELS, DEFAULT_LEVEL,
} from '../config/constants.js';
import { timeOfDay, effectiveSpeed } from './wind.js';
import { createOps, lostSeparation } from './ops.js';
import { createAlerts, hubBlocked } from './alerts.js';
import { approachControl, touchDown, stepZones } from './zones.js';
import { stepEnergy } from './energy.js';
import { incidentsEndShift } from './limits.js';
import { randomWind, FREE_DEFAULTS } from './freeplay.js';
import { dist, closestApproach, posAtTime } from './geometry.js';

export function cloneWind(wind) {
  return Object.fromEntries(TIME_SLOTS.map((slot) => [slot, { ...wind[slot] }]));
}

// Lesson 1 flies in one calm, steady wind all day, whatever the saved briefing says.
function orientationWind() {
  return Object.fromEntries(TIME_SLOTS.map((slot) => [slot, { speed: ORIENTATION_WIND_SPEED, dir: ORIENTATION_WIND_DIR }]));
}

const HUB_WAIT_DIST = 0.09; // a drone bound for a blocked pad circles this far out (#31)

export function levelOf(id) {
  return LEVELS.find((l) => l.id === id) ?? LEVELS.find((l) => l.id === DEFAULT_LEVEL);
}

/**
 * New shift state. `shift` (optional) is { kind: 'orientation' | 'standard',
 * aircraft: [type ids], level?, dayStart?, seasonDays? } for a crop-dusting
 * operations shift (#13): it adds the farmer's own aircraft and a work order.
 * Lesson 1 ('orientation') has no other traffic and a calm fixed wind.
 * `level` (a LEVELS id) sets the hub traffic; `dayStart` is the season day
 * the shift starts on, and the shift ends when the season's `seasonDays`
 * run out. Without `shift`, it's traffic only at the default level.
 */
export function createSim({ random = Math.random, wind = DEFAULT_WIND, shift = null } = {}) {
  const kind = shift?.kind ?? null;
  const level = levelOf(shift?.level);
  // free play (#46): its own fields, wind, traffic and switches
  const free = kind === 'free' ? { ...FREE_DEFAULTS, ...(shift.free ?? {}) } : null;
  const farmShift = kind === 'standard' || kind === 'free';
  let fields = [0, 1, 2, 3];
  if (kind === 'orientation') fields = ORIENTATION_FIELDS;
  else if (shift?.fields) fields = shift.fields;
  return {
    random,
    shiftKind: kind,
    ops: shift ? createOps({ fields, aircraft: shift.aircraft }) : null,
    trafficEnabled: kind !== 'orientation' && free?.traffic !== 'off',
    free,
    level: level.id,
    leadMin: level.leadMin,
    leadMax: level.leadMax,
    minSpawnInterval: level.minSpawnInterval,
    trafficCap: level.trafficCap,
    dayStart: shift?.dayStart ?? 1, // season day of this shift's first day
    seasonDays: shift?.seasonDays ?? null,
    seasonOver: false,  // the season's last day ran out during this shift
    // field and hub alerts (#31): Standard shifts only
    alerts: createAlerts({
      enabled: farmShift, random: !free || free.randomAlerts, interval: level.alertInterval, max: level.maxAlerts,
    }),
    capacityEnabled: farmShift, // landing zones, pads and patterns (#33)
    hazardsHandled: 0,  // alerts the ground crew cleared, power lines scouts found
    birdStrikes: 0,
    wind: kind === 'orientation' ? orientationWind() : cloneWind(wind),
    plans: [],          // filed, not yet airborne
    drones: [],         // airborne
    day: 1,
    elapsed: 0,         // seconds into the current day
    cleared: 0,
    adjusted: 0,        // Delay/Reroute requests applied
    holds: 0,           // Hold instructions given to airborne drones
    nearMisses: 0,      // predicted or live conflicts the player's actions removed
    preventedPairs: new Set(),  // flight pairs already credited (once per pair)
    playerMadePairs: new Set(), // conflicts the player's own adjustments created
    rewards: [],        // reward events for the UI to show and drain
    groundedTotal: 0,   // plans that hit a wind hold at least once
    separationLosses: 0, // pairs that came inside the separation ring (#13)
    separatedPairs: new Set(),
    incidents: 0,
    spawnTimer: 0,
    spawnInterval: level.spawnInterval,
    maxTraffic: level.maxTraffic,
    running: true,
    idSeq: 1,
    callsigns: {},
  };
}

/**
 * Start a new shift in place. Keeps the current wind briefing unless `wind`
 * is given; `shift` as in createSim.
 */
export function resetShift(s, { wind = s.wind, shift = null } = {}) {
  const fresh = createSim({ random: s.random, wind, shift });
  for (const key of Object.keys(s)) delete s[key];
  Object.assign(s, fresh);
}

export function windAt(s, t) {
  return s.wind[timeOfDay(t)];
}

function nextCallsign(s, prefix) {
  s.callsigns[prefix] = (s.callsigns[prefix] || 0) + 1;
  return `${prefix}-${s.callsigns[prefix] + 10}`;
}

function fieldEdgePoint(f, random) {
  const fc = { x: f.x + f.w / 2, y: f.y + f.h / 2 };
  const t = random();
  // the side of the field that faces the hubs in the middle of the farm
  if (Math.abs(fc.x - 0.5) > Math.abs(fc.y - 0.5)) {
    return { x: fc.x > 0.5 ? f.x : f.x + f.w, y: f.y + t * f.h };
  }
  return { x: f.x + t * f.w, y: fc.y > 0.5 ? f.y : f.y + f.h };
}

function makeRoute(s) {
  const outbound = s.random() < 0.5;
  const field = FIELDS[Math.floor(s.random() * FIELDS.length)];
  const hubIdx = Math.floor(s.random() * HUBS.length);
  const hub = HUBS[hubIdx];
  const hubPoint = { x: hub.x, y: hub.y };
  const fieldPoint = fieldEdgePoint(field, s.random);
  return outbound
    ? { from: hubPoint, to: fieldPoint, originLabel: hub.name, destLabel: field.name, hubEnd: 'from', hubIdx }
    : { from: fieldPoint, to: hubPoint, originLabel: field.name, destLabel: hub.name, hubEnd: 'to', hubIdx };
}

/** (Re-)file a plan against the forecast for its departure time-of-day. */
export function refile(s, p) {
  p.speed = effectiveSpeed(p.from, p.to, windAt(s, p.departAt));
  p.arriveAt = p.departAt + dist(p.from, p.to) / p.speed;
}

export function createPlan(s, { type, route, departAt }) {
  const p = {
    id: s.idSeq++,
    call: nextCallsign(s, type.prefix),
    type: type.prefix,
    maxWind: type.maxWind,
    color: type.color,
    power: type.power, // 'ev' or 'avgas' (#32)
    ...route,
    departAt,
    speed: 0,
    arriveAt: 0,
    windAtDepart: 0,
    conflicts: [],      // [{ id, call, at, minD }] predicted conflicts
    conflictWith: [],   // their callsigns
    conflictAt: null,   // where this plan's closest predicted conflict happens
    predicted: false,
    grounded: false,
    everGrounded: false,
    windHeld: false,    // came up for departure while grounded; waiting on the wind
  };
  refile(s, p);
  s.plans.push(p);
  return p;
}

/** File a random plan; `lead` (seconds to departure) defaults to a random lead for the shift's level. */
export function fileFlightPlan(s, lead = null) {
  if (s.plans.length + s.drones.length >= s.maxTraffic) return null;
  const type = TYPES[Math.floor(s.random() * TYPES.length)];
  const route = makeRoute(s);
  const wait = lead ?? s.leadMin + s.random() * (s.leadMax - s.leadMin);
  return createPlan(s, { type, route, departAt: s.elapsed + wait });
}

/**
 * The plans already on the board when a shift starts: staggered, re-drawn
 * (a few tries) until they don't conflict with each other, and random filing
 * holds off a little -- so a new player gets a calm first few launches to
 * read the scope before the traffic needs managing.
 */
export function openShift(s) {
  if (!s.trafficEnabled) return;
  for (let i = 0; i < OPENING_PLANS; i++) {
    for (let attempt = 0; attempt < 6; attempt++) {
      const p = fileFlightPlan(s, OPENING_LEAD + i * OPENING_STAGGER);
      if (!p) return;
      recomputePredictions(s);
      if (!p.predicted) break;
      s.plans = s.plans.filter((x) => x !== p);
    }
  }
  recomputePredictions(s);
  s.spawnTimer = -OPENING_LEAD;
}

/**
 * Update one time-of-day slot of the wind briefing and re-file every pending
 * plan against it, so a new forecast reads as new guidance immediately.
 */
export function setSlotWind(s, slot, { speed, dir }) {
  if (speed !== undefined) s.wind[slot].speed = Math.max(0, Math.min(MAX_WIND_INPUT, Number(speed) || 0));
  if (dir !== undefined) s.wind[slot].dir = dir;
  s.plans.forEach((p) => refile(s, p));
  recomputePredictions(s);
}

// An airborne drone's filed leg, for predicting against. The real-time engine
// can still hold it independently -- the usual gap between a filed plan and
// the actual track.
function droneLeg(d) {
  return { from: d.from, to: d.to, departAt: d.launchAt, arriveAt: d.arriveAt };
}

/**
 * Predicted conflicts for one plan against every other filed plan that isn't
 * grounded and every airborne drone: [{ id, call, at, minD }], where `at` is
 * where on this plan's leg they'd come closest. Read-only.
 */
function conflictsFor(s, p) {
  const out = [];
  const check = (leg, id, call) => {
    const ca = closestApproach(p, leg);
    if (ca && ca.minD < PREDICT_DIST) out.push({ id, call, at: posAtTime(p, ca.minT), minD: ca.minD });
  };
  for (const q of s.plans) {
    if (q.id !== p.id && !q.grounded) check(q, q.id, q.call);
  }
  for (const d of s.drones) check(droneLeg(d), d.id, d.call);
  return out;
}

/**
 * Refresh wind holds and predicted conflicts for every filed plan. A
 * prediction is a warning, not a hold: nothing here stops a launch. Conflicts
 * between two filed plans show on both, so adjusting either one clears it.
 */
export function recomputePredictions(s) {
  // Wind holds first: a grounded plan isn't going to fly, so it never counts
  // as a conflict for anyone else's plan.
  for (const p of s.plans) {
    p.windAtDepart = windAt(s, p.departAt).speed;
    p.grounded = p.windAtDepart > p.maxWind;
    if (p.grounded && !p.everGrounded) {
      p.everGrounded = true;
      s.groundedTotal++;
    }
  }
  for (const p of s.plans) {
    p.conflicts = conflictsFor(s, p);
    p.conflictWith = p.conflicts.map((c) => c.call);
    const closest = p.conflicts.reduce((best, c) => (!best || c.minD < best.minD ? c : best), null);
    p.conflictAt = closest ? closest.at : null;
    p.predicted = p.conflicts.length > 0;
  }
}

/** The hub a Reroute would switch this plan to. */
export function rerouteHub(p) {
  return HUBS[(p.hubIdx + 1) % HUBS.length];
}

// Which hub a Reroute goes to: the one asked for (a dragged route), or the
// next one along (the button and the R key).
const targetHub = (p, hubIdx) => (hubIdx === null || hubIdx === undefined ? rerouteHub(p) : HUBS[hubIdx]);

// Apply a Delay or Reroute to a plan object (the real one, or a copy for a
// preview) and re-file it against the forecast.
function applyAdjustment(s, p, kind, hubIdx = null) {
  if (kind === 'delay') {
    // A held (overdue) plan delays from now, not from its stale filed time.
    p.departAt = Math.max(p.departAt, s.elapsed) + DELAY_STEP;
  } else {
    const alt = targetHub(p, hubIdx);
    p.hubIdx = HUBS.indexOf(alt);
    if (p.hubEnd === 'from') { p.from = { x: alt.x, y: alt.y }; p.originLabel = alt.name; }
    else { p.to = { x: alt.x, y: alt.y }; p.destLabel = alt.name; }
  }
  // Re-filing can move the leg into a windier or calmer slot, changing its
  // speed and whether it's grounded, not just its start time.
  refile(s, p);
}

const pairKey = (a, b) => (a < b ? `${a}:${b}` : `${b}:${a}`);

// Score a prevented near miss between two flights, once per pair, and queue
// a reward event for the UI at the spot where they would have met.
function creditNearMiss(s, a, b, at) {
  const key = pairKey(a, b);
  if (s.preventedPairs.has(key) || s.playerMadePairs.has(key)) return;
  s.preventedPairs.add(key);
  s.nearMisses++;
  s.rewards.push({ kind: 'nearMiss', points: SCORING.nearMiss, x: at.x, y: at.y });
  if (s.rewards.length > 20) s.rewards.shift();
}

/**
 * What a Delay or Reroute would do to a plan, without changing anything: the
 * adjusted route and timing, whether the new slot grounds it, and its
 * conflicts afterwards, split into those it clears, keeps and creates.
 * `hubIdx` picks the Reroute hub (default: the next one). Returns null when
 * the plan doesn't exist.
 */
export function previewAdjustment(s, id, kind, hubIdx = null) {
  const p = s.plans.find((x) => x.id === id);
  if (!p) return null;
  const q = { ...p, from: { ...p.from }, to: { ...p.to } };
  applyAdjustment(s, q, kind, hubIdx);
  const windAtDepart = windAt(s, q.departAt).speed;
  const conflicts = conflictsFor(s, q);
  const before = new Set((p.conflicts || []).map((c) => c.id));
  const after = new Set(conflicts.map((c) => c.id));
  return {
    kind,
    id,
    hub: kind === 'reroute' ? targetHub(p, hubIdx).name : null,
    hubIdx: kind === 'reroute' ? q.hubIdx : p.hubIdx,
    from: q.from,
    to: q.to,
    hubEndAt: hubEndOf(q),
    originLabel: q.originLabel,
    destLabel: q.destLabel,
    departAt: q.departAt,
    windAtDepart,
    grounded: windAtDepart > q.maxWind,
    // landing at a hub that can't refill it means a long turnaround (#32)
    noSupply: kind === 'reroute' && q.hubEnd === 'to' && Boolean(q.power) && !HUBS[q.hubIdx].supplies.includes(q.power),
    conflicts,
    cleared: (p.conflicts || []).filter((c) => !after.has(c.id)),
    remaining: conflicts.filter((c) => before.has(c.id)),
    created: conflicts.filter((c) => !before.has(c.id)),
  };
}

// Delay or Reroute a filed plan. Every conflict the change removes is a near
// miss prevented (+points, once per pair); conflicts it creates are marked as
// the player's own, so undoing them later earns nothing.
function adjustPlan(s, id, kind, hubIdx = null) {
  const p = s.plans.find((x) => x.id === id);
  if (!p) return null;
  recomputePredictions(s);
  const before = p.conflicts;
  const beforeIds = new Set(before.map((c) => c.id));
  applyAdjustment(s, p, kind, hubIdx);
  s.adjusted++;
  recomputePredictions(s);
  const afterIds = new Set(p.conflicts.map((c) => c.id));
  for (const c of p.conflicts) {
    if (!beforeIds.has(c.id)) s.playerMadePairs.add(pairKey(p.id, c.id));
  }
  for (const c of before) {
    if (!afterIds.has(c.id)) creditNearMiss(s, p.id, c.id, c.at);
  }
  return p;
}

export function delayPlan(s, id) {
  return adjustPlan(s, id, 'delay');
}

/** Reroute a plan via another hub: `hubIdx` (a dragged route) or the next one. */
export function reroutePlan(s, id, hubIdx = null) {
  const p = s.plans.find((x) => x.id === id);
  if (!p || (hubIdx !== null && (hubIdx === p.hubIdx || !HUBS[hubIdx]))) return null;
  return adjustPlan(s, id, 'reroute', hubIdx);
}

/** The point of a plan's route that sits on a hub (the end a drag moves). */
export function hubEndOf(p) {
  return p.hubEnd === 'from' ? p.from : p.to;
}

/**
 * Put an airborne drone in a holding pattern (holding drones can't collide).
 * Holding one side of a live separation alert is a near miss prevented.
 */
export function holdDrone(s, id) {
  const d = s.drones.find((x) => x.id === id);
  if (!d || d.state === 'holding') return null;
  const wasAlert = d.state === 'conflict';
  d.state = 'holding';
  s.holds++;
  if (wasAlert) {
    for (const e of s.drones) {
      if (e !== d && e.state === 'conflict' && dist(d, e) < CONFLICT_DIST) {
        creditNearMiss(s, d.id, e.id, { x: (d.x + e.x) / 2, y: (d.y + e.y) / 2 });
      }
    }
  }
  return d;
}

export function clearDrone(s, id) {
  const d = s.drones.find((x) => x.id === id);
  if (!d || d.state !== 'holding') return null;
  d.state = 'enroute';
  return d;
}

function launch(s, p) {
  s.drones.push({
    id: p.id, call: p.call, color: p.color, type: p.type,
    x: p.from.x, y: p.from.y, from: p.from, to: p.to,
    originLabel: p.originLabel, destLabel: p.destLabel, hubEnd: p.hubEnd, hubIdx: p.hubIdx, power: p.power,
    state: 'enroute', holdAngle: s.random() * Math.PI * 2, trail: [],
    launchAt: p.departAt, arriveAt: p.arriveAt, speed: p.speed,
  });
}

/** The season day the shift is on (1 = the season's first). */
export function seasonDay(s) {
  return s.dayStart + s.day - 1;
}

/** Live (airborne) conflict pairs, as callsign pairs. */
export function liveConflictPairs(s) {
  const confl = s.drones.filter((d) => d.state === 'conflict');
  const pairs = [];
  for (let i = 0; i < confl.length; i++) {
    for (let j = i + 1; j < confl.length; j++) {
      if (dist(confl[i], confl[j]) < CONFLICT_DIST) pairs.push([confl[i].call, confl[j].call]);
    }
  }
  return pairs;
}

export function step(s, dt) {
  if (!s.running) return;
  s.elapsed += dt;

  if (s.elapsed > DAY_LENGTH) {
    // the season's last day is over: the shift ends with it
    if (s.seasonDays !== null && seasonDay(s) >= s.seasonDays) {
      s.elapsed = DAY_LENGTH;
      s.seasonOver = true;
      s.running = false;
      return;
    }
    s.elapsed -= DAY_LENGTH;
    s.day += 1;
    // free play (#46): new weather every day
    if (s.free?.randomWeather) {
      const next = randomWind(s.random);
      for (const slot of TIME_SLOTS) s.wind[slot] = next[slot];
    }
    s.spawnInterval = Math.max(s.minSpawnInterval, s.spawnInterval - SPAWN_INTERVAL_STEP);
    s.maxTraffic = Math.min(s.trafficCap, s.maxTraffic + 1);
    // the clock wrapped: pull every plan and drone into the new day's frame
    for (const p of s.plans) { p.departAt -= DAY_LENGTH; refile(s, p); }
    for (const d of s.drones) { d.launchAt -= DAY_LENGTH; d.arriveAt -= DAY_LENGTH; }
  }

  s.spawnTimer += dt;
  if (s.trafficEnabled && s.spawnTimer >= s.spawnInterval) {
    s.spawnTimer = 0;
    fileFlightPlan(s);
  }

  // A grounded plan coming up for departure is kept WIND_RELEASE_LEAD seconds out and
  // re-filed every tick: the hold lifts by itself once the slot it would leave
  // in is calm enough, and the player still gets a short countdown (and a
  // conflict warning) before it actually launches.
  for (const p of s.plans) {
    const holdLine = s.elapsed + WIND_RELEASE_LEAD;
    // its hub's pad is blocked (#31): it waits, like a wind hold, until it's clear
    p.hubHeld = hubBlocked(s, p.hubIdx);
    if (p.hubHeld && p.departAt < holdLine) { p.departAt = holdLine; refile(s, p); continue; }
    if (p.grounded && p.departAt < holdLine) { p.departAt = holdLine; p.windHeld = true; refile(s, p); }
    else if (p.departAt < s.elapsed) { p.departAt = s.elapsed; refile(s, p); }
  }

  recomputePredictions(s);

  // Launch every due plan that isn't grounded by wind. A predicted conflict
  // doesn't stop it: only the player's Delay or Reroute does.
  const stillFiled = [];
  for (const p of s.plans) {
    if (p.departAt <= s.elapsed && !p.grounded) launch(s, p);
    else stillFiled.push(p);
  }
  // what just launched changes the picture for everything still filed
  if (stillFiled.length < s.plans.length) { s.plans = stillFiled; recomputePredictions(s); }

  // movement (airborne)
  for (const d of s.drones) {
    if (d.state === 'landed') continue; // on a pad for its turnaround (#33)
    if (s.capacityEnabled) {
      // near its landing zone: cleared to a pad, or into the pattern (#33)
      approachControl(s, d);
    } else {
      // bound for a blocked pad (#31): circle near the hub until it's clear
      const blocked = d.hubEnd === 'to' && hubBlocked(s, d.hubIdx);
      if (blocked && d.state !== 'holding' && dist(d, d.to) < HUB_WAIT_DIST) { d.state = 'holding'; d.padWait = true; }
      if (!blocked && d.padWait) { d.padWait = false; d.state = 'enroute'; }
    }
    if (d.state === 'holding') { d.holdAngle += dt * 1.6; continue; }
    const dx = d.to.x - d.x;
    const dy = d.to.y - d.y;
    const dd = Math.hypot(dx, dy);
    if (dd < ARRIVE_DIST) {
      if (s.capacityEnabled) touchDown(s, d);
      else d.arrived = true;
      continue;
    }
    d.x += (dx / dd) * d.speed * dt;
    d.y += (dy / dd) * d.speed * dt;
    d.trail.push({ x: d.x, y: d.y });
    if (d.trail.length > 10) d.trail.shift();
  }
  s.cleared += s.drones.filter((d) => d.arrived).length;
  s.drones = s.drones.filter((d) => !d.arrived);

  // real-time separation / collision among airborne traffic
  const live = new Set();
  for (let i = 0; i < s.drones.length; i++) {
    for (let j = i + 1; j < s.drones.length; j++) {
      const a = s.drones[i];
      const b = s.drones[j];
      if (a.state === 'holding' || b.state === 'holding' || a.state === 'landed' || b.state === 'landed') continue;
      const d = dist(a, b);
      if (d < COLLISION_DIST) { a.collided = true; b.collided = true; }
      else if (d < CONFLICT_DIST) { live.add(a.id); live.add(b.id); lostSeparation(s, a, b); }
    }
  }
  for (const d of s.drones) {
    if (d.state !== 'holding' && d.state !== 'landed') d.state = live.has(d.id) ? 'conflict' : 'enroute';
  }

  if (s.drones.some((d) => d.collided)) {
    s.incidents += 1;
    s.drones = s.drones.filter((d) => !d.collided);
    if (incidentsEndShift(s)) s.running = false;
  }

  stepZones(s, dt);
  stepEnergy(s);
}
